import { Cell } from '@ptml/design-system';
import { memo } from 'react';
import { FourZeroFour } from './FourZeroFour.svg';
import {
    Container,
    ImageContainer,
    Supertitle,
    Title,
} from './GenericError.styles';
import { Userpilot } from 'userpilot';
import {
    FeatureFlags,
    useFeatureFlag,
} from '@repairer-frontend/client-feature-flags';
import { ERROR_GRAPHIC_ALTERNATE_TEXT_REBRAND } from '../../../../constants';
import { sharedUiI18n } from '../../../../utils/i18n';

type GenericErrorProperties = {
    error?: Error;
    is404?: boolean;
};

export const GenericError = memo(
    ({ error, is404 = false }: GenericErrorProperties) => {
        // Use shared-ui's i18n instance directly instead of specific app instance
        const translate = sharedUiI18n.t.bind(sharedUiI18n);

        const titles = [translate('FLAT_TIRE'), translate('WRONG_TURN')];

        const randomTitle = Math.floor(
            Math.random() * Object.keys(titles).length,
        );
        const title = Object.values(titles)[randomTitle];

        const imageUrl = '/tires.jpg';

        const isUserPilotEnabled = useFeatureFlag(
            FeatureFlags.isUserPilotEnabled,
        );

        const getNewRelicTraceIds = () => {
            try {
                if (typeof window !== 'undefined' && (window as any).newrelic) {
                    return Object.keys(
                        (window as any).newrelic.initializedAgents || {},
                    );
                }
            } catch (error) {
                console.warn('Failed to get New Relic trace ID: ', error);
                return null;
            }
        };
        if (isUserPilotEnabled) {
            // Get session trace IDs using the New Relic browser agent API (1 expected)
            const traceIds = getNewRelicTraceIds();
            Userpilot.track('GenericError', {
                // Event metadata
                pathname: window.location.pathname,
                newRelicTraceId: traceIds?.join(', ') || '',
            });
        }

        if (error) {
            console.error(error);
        }

        return (
            <Container role="alert">
                <Cell rowStart={1} colStart={1}>
                    <Supertitle>
                        {is404 ? translate('ERROR_404') : translate('ERROR')}
                    </Supertitle>
                </Cell>
                <Cell colStart={1} rowStart={2}>
                    <Title>{is404 ? translate('NOT_FOUND') : title}</Title>
                </Cell>
                <Cell colStart={2} rowStart={2}>
                    <ImageContainer>
                        {is404 ? (
                            <FourZeroFour />
                        ) : (
                            <img
                                src={imageUrl}
                                data-testid={'orderly-error-image'}
                                alt={ERROR_GRAPHIC_ALTERNATE_TEXT_REBRAND}
                            />
                        )}
                    </ImageContainer>
                </Cell>
            </Container>
        );
    },
);

export default GenericError;
GenericError.displayName = 'GenericError';
